import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const StarButton = ({ label, value, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full focus:outline-none focus:ring-2 focus:ring-rose-300 ${
      active ? "text-amber-400" : "text-slate-200"
    }`}
    aria-label={`${label} ${value} stars`}
  >
    <span className="text-3xl leading-none">{"\u2605"}</span>
  </button>
);

const DEFAULT_REVIEW_QUESTION_CONFIG = {
  overall: { label: "Overall experience", enabled: true, required: true },
  salon: { label: "Salon atmosphere", enabled: true, required: true },
  reception: { label: "Reception", enabled: true, required: true },
  service: { label: "Service quality", enabled: true, required: true },
  staff: { label: "Staff professionalism", enabled: true, required: true },
  online_booking: { label: "Online booking", enabled: true, required: false },
  technician_service: {
    label: "Service quality",
    enabled: true,
    required: false,
  },
  technician_staff: {
    label: "Staff professionalism",
    enabled: true,
    required: false,
  },
};

const normalizeReviewQuestionConfig = (value) => {
  const source = value && typeof value === "object" ? value : {};
  return Object.fromEntries(
    Object.entries(DEFAULT_REVIEW_QUESTION_CONFIG).map(([key, defaults]) => {
      const incoming =
        source[key] && typeof source[key] === "object" ? source[key] : {};
      const required = Boolean(incoming.required ?? defaults.required);
      return [
        key,
        {
          ...defaults,
          label: String(incoming.label || defaults.label).trim() || defaults.label,
          enabled: required || Boolean(incoming.enabled ?? defaults.enabled),
          required,
        },
      ];
    })
  );
};

const parseQuestionConfig = (value) => {
  if (!value) return DEFAULT_REVIEW_QUESTION_CONFIG;
  try {
    return JSON.parse(value);
  } catch (err) {
    return DEFAULT_REVIEW_QUESTION_CONFIG;
  }
};

const RatingRow = ({ label, value, onChange, allowClear = false, required = false }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
      <span>{label}</span>
      {required && <span className="text-xs font-normal text-rose-500">Required</span>}
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <StarButton
          key={`${label}-${star}`}
          label={label}
          value={star}
          active={value >= star}
          onClick={() => onChange(star)}
        />
      ))}
      <span className="text-xs text-slate-500">
        {value ? `${value}/5` : "Select"}
      </span>
      {allowClear && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs font-semibold text-slate-500 hover:text-slate-700"
        >
          Not applicable
        </button>
      )}
    </div>
  </div>
);

export default function PortalReviewPreview() {
  const [searchParams] = useSearchParams();
  const salonName = searchParams.get("salon_name") || "Your Salon";
  const technicianName = searchParams.get("technician_name") || "Helen C";
  const serviceName = searchParams.get("service_name") || "BIOGEL REFILL";
  const formTitle = searchParams.get("form_title") || "Client Feedback";
  const formSubtitle =
    searchParams.get("form_subtitle") ||
    "Thank you for taking a moment to share your experience.";
  const commentEnabled = searchParams.get("comment_enabled") !== "0";
  const commentLabel = searchParams.get("comment_label") || "Additional comments";
  const commentRequiredBelow = Number(
    searchParams.get("comment_required_below_rating") || 4
  );
  const commentRequiredMessage =
    searchParams.get("comment_required_message") ||
    "We're sorry it wasn't perfect - could you share a quick note so we can improve?";
  const thankYouTitle =
    searchParams.get("thank_you_title") || "Feedback received. Thank you!";
  const thankYouMessage =
    searchParams.get("thank_you_message") ||
    "Thank you - your feedback was sent to the salon.";
  const publicReviewCtaText =
    searchParams.get("public_review_cta_text") || "Leave a public review";
  const publicReviewHelperText =
    searchParams.get("public_review_helper_text") ||
    "If you have a moment, we would appreciate a public review.";
  const googleReviewUrl = searchParams.get("google_review_url") || "";
  const publicReviewEnabled = searchParams.get("public_review_enabled") !== "0";
  const publicReviewMode = searchParams.get("public_review_mode") || "immediate";
  const publicReviewThreshold = Number(
    searchParams.get("public_review_threshold") || 4
  );
  const bookedViaPortal = searchParams.get("booked_via_portal") !== "0";
  const questions = normalizeReviewQuestionConfig(
    parseQuestionConfig(searchParams.get("question_config"))
  );
  const enabled = (key) => questions[key]?.enabled !== false;
  const required = (key) => Boolean(questions[key]?.required);
  const label = (key) => questions[key]?.label || DEFAULT_REVIEW_QUESTION_CONFIG[key]?.label;

  const [ratings, setRatings] = useState({
    overall: null,
    salon: null,
    reception: null,
    service: null,
    staff: null,
    onlineBooking: null,
    technicianService: null,
    technicianStaff: null,
  });
  const [comment, setComment] = useState("");
  const [technicianComment, setTechnicianComment] = useState("");
  const [allowContact, setAllowContact] = useState(true);
  const [requestCallback, setRequestCallback] = useState(false);
  const [preferredCallbackTime, setPreferredCallbackTime] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const visitDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    []
  );

  const canSubmit =
    ratings.overall &&
    (!enabled("salon") || !required("salon") || ratings.salon) &&
    (!enabled("reception") || !required("reception") || ratings.reception) &&
    (!enabled("service") || !required("service") || ratings.service) &&
    (!enabled("staff") || !required("staff") || ratings.staff) &&
    (!bookedViaPortal ||
      !enabled("online_booking") ||
      !required("online_booking") ||
      ratings.onlineBooking) &&
    (!enabled("technician_service") ||
      !required("technician_service") ||
      ratings.technicianService) &&
    (!enabled("technician_staff") ||
      !required("technician_staff") ||
      ratings.technicianStaff) &&
    (!commentEnabled ||
      !ratings.overall ||
      ratings.overall >= commentRequiredBelow ||
      comment.trim());
  const showPublicReview =
    submitted &&
    publicReviewEnabled &&
    publicReviewMode !== "delayed_email" &&
    ratings.overall >= publicReviewThreshold;

  const updateRating = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      setSubmitError("Please rate all required categories before submitting.");
      return;
    }
    setSubmitError("");
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-3xl">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl font-semibold text-white md:text-2xl">
              {formTitle}
            </h1>
            <p className="text-sm text-rose-100">
              {formSubtitle}
            </p>
          </div>
          <div className="space-y-6 px-6 py-6">
            <div className="space-y-1 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm text-slate-500">Salon</div>
              <div className="text-lg font-semibold text-slate-900">
                {salonName}
              </div>
              <div className="text-sm text-slate-600">
                Visit date: {visitDate}
              </div>
              <div className="text-sm text-slate-600">
                Service performed: {serviceName}
              </div>
              <div className="text-sm text-slate-600">
                Technician: {technicianName}
              </div>
            </div>

            {submitted ? (
              <div className="space-y-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                <div className="text-lg font-semibold text-emerald-900">
                  {thankYouTitle}
                </div>
                <div className="text-sm text-emerald-800">{thankYouMessage}</div>
                {showPublicReview && (
                  <div className="space-y-2">
                    <div className="text-sm text-emerald-800">
                      {publicReviewHelperText}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        googleReviewUrl && window.open(googleReviewUrl, "_blank")
                      }
                      className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                    >
                      {publicReviewCtaText}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {submitError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 md:text-base">
                    {submitError}
                  </div>
                )}
                <RatingRow
                  label={label("overall")}
                  value={ratings.overall}
                  onChange={(value) => updateRating("overall", value)}
                  required
                />
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {enabled("salon") && (
                    <RatingRow
                      label={label("salon")}
                      value={ratings.salon}
                      onChange={(value) => updateRating("salon", value)}
                      required={required("salon")}
                    />
                  )}
                  {enabled("reception") && (
                    <RatingRow
                      label={label("reception")}
                      value={ratings.reception}
                      onChange={(value) => updateRating("reception", value)}
                      required={required("reception")}
                    />
                  )}
                  {enabled("service") && (
                    <RatingRow
                      label={label("service")}
                      value={ratings.service}
                      onChange={(value) => updateRating("service", value)}
                      required={required("service")}
                    />
                  )}
                  {enabled("staff") && (
                    <RatingRow
                      label={label("staff")}
                      value={ratings.staff}
                      onChange={(value) => updateRating("staff", value)}
                      required={required("staff")}
                    />
                  )}
                  {bookedViaPortal && enabled("online_booking") && (
                    <RatingRow
                      label={label("online_booking")}
                      value={ratings.onlineBooking}
                      onChange={(value) => updateRating("onlineBooking", value)}
                      allowClear
                      required={required("online_booking")}
                    />
                  )}
                </div>

                {(enabled("technician_service") || enabled("technician_staff")) && (
                <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-sm font-semibold text-slate-700">
                    Rate your technician(s)
                  </div>
                  <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-semibold text-slate-900">
                      {technicianName}
                    </div>
                    <div className="text-xs text-slate-600">
                      Service performed: {serviceName}
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {enabled("technician_service") && (
                        <RatingRow
                          label={label("technician_service")}
                          value={ratings.technicianService}
                          onChange={(value) =>
                            updateRating("technicianService", value)
                          }
                          required={required("technician_service")}
                        />
                      )}
                      {enabled("technician_staff") && (
                        <RatingRow
                          label={label("technician_staff")}
                          value={ratings.technicianStaff}
                          onChange={(value) =>
                            updateRating("technicianStaff", value)
                          }
                          required={required("technician_staff")}
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-slate-700">
                        Comment (optional)
                      </div>
                      <textarea
                        value={technicianComment}
                        onChange={(event) =>
                          setTechnicianComment(event.target.value)
                        }
                        rows={3}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        placeholder="Share feedback for this technician."
                      />
                    </div>
                  </div>
                </div>
                )}

                {commentEnabled && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-slate-700">
                      {commentLabel}
                      {ratings.overall && ratings.overall < commentRequiredBelow ? (
                        <span className="ml-2 text-xs font-normal text-rose-500">
                          Required
                        </span>
                      ) : null}
                    </div>
                    <textarea
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      rows={4}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                      placeholder="Share anything else you would like us to know."
                    />
                    {ratings.overall && ratings.overall < commentRequiredBelow ? (
                      <div className="text-xs text-slate-500">
                        {commentRequiredMessage}
                      </div>
                    ) : null}
                  </div>
                )}

                <label className="flex items-start gap-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={allowContact}
                    onChange={(event) => setAllowContact(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  <span>
                    It is okay for the salon to contact me about this feedback.
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={requestCallback}
                    onChange={(event) => setRequestCallback(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  <span>Request a manager call.</span>
                </label>
                {requestCallback && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-slate-700">
                      Preferred callback time
                    </div>
                    <input
                      type="text"
                      value={preferredCallbackTime}
                      onChange={(event) =>
                        setPreferredCallbackTime(event.target.value)
                      }
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                      placeholder="e.g. Weekdays after 5pm"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="inline-flex items-center justify-center rounded-full bg-rose-500 px-6 py-3 text-sm font-semibold text-white hover:bg-rose-600 md:text-base"
                >
                  Submit feedback
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
